import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import moment from 'moment-timezone'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { BusinessUserType } from '__generated__/api-types-and-hooks'
import { getUserCalendarEventsByUserIdAction, setUserMeetingAction } from 'store/actions/calendar'
import { formatTimeString, addMinutesToTime, isValidEmail } from '../helpers'

interface IUseMeetingHandlersProps {
  selectedGuests: IAssigneeOptions[]
  setSelectedGuests: (guests: IAssigneeOptions[]) => void
  guestSearchQuery: string
  setGuestSearchQuery: (query: string) => void
  setShowGuestDropdown: (show: boolean) => void
  setEmailError: (error: string) => void
  setHighlightedGuestIndex: (index: number) => void
  fetchedGuestsRef: React.MutableRefObject<Set<string>>
  pendingGuestRequests: React.MutableRefObject<Record<string, boolean>>
  setSelectedDate: (date: Date) => void
  setShowCalendar: (show: boolean) => void
  setShowStartTimePicker: (show: boolean) => void
  setShowEndTimePicker: (show: boolean) => void
  setStartTime: (time: string) => void
  setEndTime: (time: string) => void
  endTime: string
  setTemporarySlot: (slot: { date: Date; startHour: number; startMinute: number } | null) => void
  isCalendarClickRef: React.MutableRefObject<boolean>
  meetingLink: string
  setLinkCopied: (copied: boolean) => void
  filteredUsers: IAssigneeOptions[]
  highlightedGuestIndex: number
  meetingTitle: string
  selectedDate: Date | null
  startTime: string
  selectedTimezone: string
  currentUser: any
  tenantId: string
  isAdvisor: boolean
  meetingDescription: string
  meetingLocation: string
  onClose: () => void
  setShowCancelConfirmation: (show: boolean) => void
  setMeetingTitle: (title: string) => void
  setMeetingLink: (link: string) => void
  setMeetingDescription: (desc: string) => void
  setMeetingLocation: (location: string) => void
}

export const useMeetingHandlers = ({
  selectedGuests,
  setSelectedGuests,
  guestSearchQuery,
  setGuestSearchQuery,
  setShowGuestDropdown,
  setEmailError,
  setHighlightedGuestIndex,
  fetchedGuestsRef,
  pendingGuestRequests,
  setSelectedDate,
  setShowCalendar,
  setShowStartTimePicker,
  setShowEndTimePicker,
  setStartTime,
  setEndTime,
  endTime,
  setTemporarySlot,
  isCalendarClickRef,
  meetingLink,
  setLinkCopied,
  filteredUsers,
  highlightedGuestIndex,
  meetingTitle,
  selectedDate,
  startTime,
  selectedTimezone,
  currentUser,
  tenantId,
  isAdvisor,
  meetingDescription,
  meetingLocation,
  onClose,
  setShowCancelConfirmation,
  setMeetingTitle,
  setMeetingLink,
  setMeetingDescription,
  setMeetingLocation,
}: IUseMeetingHandlersProps) => {
  const dispatch = useDispatch()

  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date)
      setShowCalendar(false)
      setShowStartTimePicker(false)
      setShowEndTimePicker(false)
    },
    [setSelectedDate, setShowCalendar, setShowStartTimePicker, setShowEndTimePicker]
  )

  const handleStartTimeSelect = useCallback(
    (time: string) => {
      setStartTime(time)
      setShowStartTimePicker(false)

      if (!endTime || endTime <= time) {
        const endTimeString = addMinutesToTime(time, 60)
        setEndTime(endTimeString)
      }
    },
    [setStartTime, setShowStartTimePicker, endTime, setEndTime]
  )

  const handleEndTimeSelect = useCallback(
    (time: string) => {
      setEndTime(time)
      setShowEndTimePicker(false)
    },
    [setEndTime, setShowEndTimePicker]
  )

  const handleGuestSelect = useCallback(
    (guest: IAssigneeOptions) => {
      setSelectedGuests([...selectedGuests, guest])
      setGuestSearchQuery('')
      setShowGuestDropdown(false)
      setEmailError('')
      setHighlightedGuestIndex(-1)
    },
    [
      selectedGuests,
      setSelectedGuests,
      setGuestSearchQuery,
      setShowGuestDropdown,
      setEmailError,
      setHighlightedGuestIndex,
    ]
  )

  const handleRemoveGuest = useCallback(
    (guestValue: string) => {
      setSelectedGuests(selectedGuests.filter((guest) => guest.value !== guestValue))
      fetchedGuestsRef.current.delete(guestValue)

      if (pendingGuestRequests.current[guestValue]) {
        delete pendingGuestRequests.current[guestValue]
      }

      dispatch(getUserCalendarEventsByUserIdAction.RESET())
    },
    [selectedGuests, setSelectedGuests, fetchedGuestsRef, pendingGuestRequests, dispatch]
  )

  const handleAddGuestByEmail = useCallback(() => {
    const email = guestSearchQuery.trim()

    if (!email) return

    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }

    if (selectedGuests.some((guest) => guest.value === email)) {
      setEmailError('This email is already invited')
      return
    }

    const externalGuest: IAssigneeOptions = {
      label: email,
      value: email,
      type: BusinessUserType.Member,
      user: {
        id: email,
        firstName: email.split('@')[0],
        lastName: '',
      },
    }

    setSelectedGuests([...selectedGuests, externalGuest])
    setGuestSearchQuery('')
    setShowGuestDropdown(false)
    setEmailError('')
  }, [
    guestSearchQuery,
    selectedGuests,
    setSelectedGuests,
    setGuestSearchQuery,
    setShowGuestDropdown,
    setEmailError,
  ])

  const handleTimeSlotClick = useCallback(
    (date: Date, hour: number, startMinute: number) => {
      isCalendarClickRef.current = true
      setSelectedDate(new Date(date))

      setTemporarySlot({
        date: new Date(date),
        startHour: hour,
        startMinute: startMinute,
      })

      const startTimeString = formatTimeString(hour, startMinute)
      setStartTime(startTimeString)

      const endTimeString = addMinutesToTime(startTimeString, 15)
      setEndTime(endTimeString)
    },
    [isCalendarClickRef, setSelectedDate, setTemporarySlot, setStartTime, setEndTime]
  )

  const handleCopyMeetingLink = useCallback(async () => {
    if (!meetingLink.trim()) return

    try {
      await navigator.clipboard.writeText(meetingLink)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch (error) {
      // Failed to copy to clipboard
    }
  }, [meetingLink, setLinkCopied])

  const handleGuestInputKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (filteredUsers.length > 0 && highlightedGuestIndex >= 0) {
          handleGuestSelect(filteredUsers[highlightedGuestIndex])
        } else if (filteredUsers.length === 0) {
          handleAddGuestByEmail()
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedGuestIndex(
          highlightedGuestIndex < filteredUsers.length - 1 ? highlightedGuestIndex + 1 : 0
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedGuestIndex(
          highlightedGuestIndex > 0 ? highlightedGuestIndex - 1 : filteredUsers.length - 1
        )
      } else if (e.key === 'Escape') {
        setShowGuestDropdown(false)
        setHighlightedGuestIndex(-1)
      }
    },
    [
      filteredUsers,
      highlightedGuestIndex,
      handleGuestSelect,
      handleAddGuestByEmail,
      setHighlightedGuestIndex,
      setShowGuestDropdown,
    ]
  )

  const handleSuggestedTimeClick = useCallback(
    (suggestion: { date: string; time: string; dateTime: Date }) => {
      setSelectedDate(suggestion.dateTime)

      const hours = suggestion.dateTime.getHours()
      const minutes = suggestion.dateTime.getMinutes()
      const startTimeString = formatTimeString(hours, minutes)
      setStartTime(startTimeString)

      const endDateTime = new Date(suggestion.dateTime)
      endDateTime.setMinutes(minutes + 30)
      const endTimeString = formatTimeString(endDateTime.getHours(), endDateTime.getMinutes())
      setEndTime(endTimeString)
    },
    [setSelectedDate, setStartTime, setEndTime]
  )

  const handleCancelClick = useCallback(() => {
    const hasDescriptionContent =
      meetingDescription &&
      meetingDescription !== '<p></p>' &&
      meetingDescription.replace(/<[^>]*>/g, '').trim().length > 0

    const hasUnsavedData =
      meetingTitle ||
      selectedDate ||
      startTime ||
      endTime ||
      selectedGuests.length > 0 ||
      meetingLink ||
      meetingLocation ||
      hasDescriptionContent

    if (hasUnsavedData) {
      setShowCancelConfirmation(true)
    } else {
      onClose()
    }
  }, [
    meetingTitle,
    selectedDate,
    startTime,
    endTime,
    selectedGuests,
    meetingLink,
    meetingLocation,
    meetingDescription,
    setShowCancelConfirmation,
    onClose,
  ])

  const handleConfirmCancel = useCallback(() => {
    setShowCancelConfirmation(false)
    setMeetingTitle('')
    setMeetingLink('')
    setMeetingDescription('')
    setMeetingLocation('')
    setLinkCopied(false)
    onClose()
  }, [
    setShowCancelConfirmation,
    setMeetingTitle,
    setMeetingLink,
    setMeetingDescription,
    setMeetingLocation,
    setLinkCopied,
    onClose,
  ])

  const handleCancelConfirmation = useCallback(() => {
    setShowCancelConfirmation(false)
  }, [setShowCancelConfirmation])

  const handleCreateMeeting = useCallback(() => {
    if (!meetingTitle.trim()) {
      alert('Please enter a meeting title')
      return
    }

    if (!selectedDate || !startTime || !endTime) {
      alert('Please select date and time for the meeting')
      return
    }

    if (selectedGuests.length === 0) {
      alert('Please add at least one attendee')
      return
    }

    const startDateTime = moment(selectedDate)
      .set({
        hour: parseInt(startTime.split(':')[0]),
        minute: parseInt(startTime.split(':')[1]),
        second: 0,
        millisecond: 0,
      })
      .tz(selectedTimezone)
      .toISOString()

    const endDateTime = moment(selectedDate)
      .set({
        hour: parseInt(endTime.split(':')[0]),
        minute: parseInt(endTime.split(':')[1]),
        second: 0,
        millisecond: 0,
      })
      .tz(selectedTimezone)
      .toISOString()

    const attendees: string[] = []
    const teamMemberList: string[] = []

    selectedGuests.forEach((guest) => {
      const isExternal = guest.value.includes('@')
      if (isExternal) {
        attendees.push(guest.value)
      } else {
        teamMemberList.push(guest.value)
      }
    })

    const participantId =
      selectedGuests.length > 0 ? selectedGuests[0].value : currentUser?.id || ''
    const participantName =
      selectedGuests.length > 0 ? selectedGuests[0].label : currentUser?.name || ''

    let meetingLocationTool = 'MICROSOFT_TEAMS'
    if (meetingLink) {
      if (meetingLink.includes('zoom')) {
        meetingLocationTool = 'ZOOM'
      } else if (meetingLink.includes('meet.google')) {
        meetingLocationTool = 'GOOGLE_MEET'
      } else if (!meetingLink.includes('teams.microsoft')) {
        meetingLocationTool = 'CUSTOM'
      }
    }

    const payload = {
      tenantId: tenantId || '',
      title: meetingTitle.trim(),
      description: meetingDescription || '',
      startTime: startDateTime,
      endTime: endDateTime,
      participantId,
      participantName,
      organizerName:
        `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() ||
        currentUser?.name ||
        '',
      advisorName: isAdvisor
        ? `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() ||
          currentUser?.name ||
          ''
        : undefined,
      location: meetingLocation || undefined,
      meetingLocationTool,
      videoLinkDescription: meetingLink || undefined,
      timezone: selectedTimezone,
      attendees: attendees.length > 0 ? attendees : undefined,
      teamMemberList: teamMemberList.length > 0 ? teamMemberList : undefined,
      role: currentUser?.roles || [],
      ignoreConflicts: false,
    }

    dispatch(setUserMeetingAction.STARTED(payload))
    onClose()
  }, [
    meetingTitle,
    selectedDate,
    startTime,
    endTime,
    selectedGuests,
    selectedTimezone,
    currentUser,
    tenantId,
    isAdvisor,
    meetingDescription,
    meetingLocation,
    meetingLink,
    dispatch,
    onClose,
  ])

  return {
    handleDateSelect,
    handleStartTimeSelect,
    handleEndTimeSelect,
    handleGuestSelect,
    handleRemoveGuest,
    handleAddGuestByEmail,
    handleTimeSlotClick,
    handleCopyMeetingLink,
    handleGuestInputKeyPress,
    handleSuggestedTimeClick,
    handleCancelClick,
    handleConfirmCancel,
    handleCancelConfirmation,
    handleCreateMeeting,
  }
}
